/**
 * Embedding service for generating and managing policy embeddings
 * Uses OpenAI text-embedding-3-small model
 */

import { getApiKey } from '@/ai/providers/openai';

const EMBEDDING_MODEL = 'text-embedding-3-small';
const EMBEDDING_DIMENSIONS = 1536;

export interface EmbeddingResponse {
  embedding: number[];
  tokenCount: number;
}

/**
 * Generate embedding vector for a given text using OpenAI API
 * Uses the user's BYOK API key from localStorage
 * 
 * @param text - Text to embed
 * @returns Embedding vector and token count
 */
export async function generateEmbedding(text: string): Promise<EmbeddingResponse> {
  if (!text || !text.trim()) {
    throw new Error('Text cannot be empty');
  }

  try {
    const apiKey = getApiKey();
    
    const response = await fetch('https://api.openai.com/v1/embeddings', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`
      },
      body: JSON.stringify({
        model: EMBEDDING_MODEL,
        input: text.trim(),
        dimensions: EMBEDDING_DIMENSIONS
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        `OpenAI API error: ${response.status} - ${errorData.error?.message || response.statusText}`
      );
    }

    const data = await response.json();
    
    if (!data.data || !data.data[0] || !data.data[0].embedding) {
      throw new Error('Invalid response from OpenAI API');
    }

    return {
      embedding: data.data[0].embedding,
      tokenCount: data.usage?.total_tokens || 0
    };
  } catch (error: any) {
    console.error('Error generating embedding:', error);
    throw new Error(`Failed to generate embedding: ${error.message}`);
  }
}

/**
 * Generate embeddings for multiple texts in batch
 * More efficient than calling generateEmbedding multiple times
 * 
 * @param texts - Array of texts to embed
 * @returns Array of embedding responses
 */
export async function generateEmbeddingsBatch(
  texts: string[]
): Promise<EmbeddingResponse[]> {
  if (!texts || texts.length === 0) {
    return [];
  }

  // Filter out empty texts
  const validTexts = texts.filter(t => t && t.trim());
  
  if (validTexts.length === 0) {
    return [];
  }

  try {
    const apiKey = getApiKey();
    
    const response = await fetch('https://api.openai.com/v1/embeddings', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`
      },
      body: JSON.stringify({
        model: EMBEDDING_MODEL,
        input: validTexts,
        dimensions: EMBEDDING_DIMENSIONS
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        `OpenAI API error: ${response.status} - ${errorData.error?.message || response.statusText}`
      );
    }

    const data = await response.json();
    
    if (!data.data || data.data.length !== validTexts.length) {
      throw new Error('Invalid response from OpenAI API');
    }

    return data.data.map((item: any, index: number) => ({
      embedding: item.embedding,
      tokenCount: Math.ceil(data.usage?.total_tokens / validTexts.length) || 0
    }));
  } catch (error: any) {
    console.error('Error generating embeddings batch:', error);
    throw new Error(`Failed to generate embeddings: ${error.message}`);
  }
}

/**
 * Validate if an API key is configured and accessible
 */
export function hasEmbeddingAPIKey(): boolean {
  try {
    getApiKey();
    return true;
  } catch {
    return false;
  }
}

/**
 * Get embedding model information
 */
export function getEmbeddingModelInfo() {
  return {
    model: EMBEDDING_MODEL,
    dimensions: EMBEDDING_DIMENSIONS,
    maxTokens: 8191, // Max input tokens for text-embedding-3-small
    costPer1kTokens: 0.00002 // USD
  };
}

